// src/services/chat/chatService.js

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  writeBatch,
  setDoc,
  updateDoc,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { ChatMessage, MessageStatus, MessageType } from '../../models/chatModels/chatMessage';
import { ChatConversation } from '../../models/chatModels/chatConversation';
import { chatLocalDb } from './chatLocalDb';

/**
 * ChatService (Firebase-backed)
 *
 * Manages the conversations list. For per-conversation message streaming,
 * subscribe via getMessagesStream(conversationId, callback).
 *
 * Firestore structure:
 *   /chats/{conversationId}
 *       participants, participantsKey, lastMessage, lastMessageSender,
 *       lastMessageTime, lastMessageType, lastMessageStatus,
 *       unreadCount (map uid->int), isArchived, isMuted, isPinned,
 *       mutedUntil, lastSeen (map uid->Timestamp), createdAt,
 *       deletedAt, deletedBy
 *
 *   /chats/{conversationId}/messages/{messageId}
 *       from, to, msg, ts, msgType, status, attachmentUrl, attachmentType,
 *       seenBy, isDeleted
 */
class ChatService {
  constructor() {
    this._currentUserId = null;
    this._initialized = false;

    this._messageListeners = new Map(); // conversationId -> unsubscribe fn

    // Simple pub/sub replacing Dart StreamControllers.
    this._messageSubs = new Set();
    this._errorSubs = new Set();
    this._connectionSubs = new Set();
    this._conversationsSubs = new Set();

    this._conversationsUnsub = null;
  }

  // ── Singleton ────────────────────────────────────────────────────────────

  static getInstance() {
    if (!ChatService._instance) {
      ChatService._instance = new ChatService();
    }
    return ChatService._instance;
  }

  // ── Public subscription API (replaces Dart Streams) ───────────────────────
  // Each returns an unsubscribe function. Usage:
  //   const unsub = chatService.onMessage((msg) => ...);
  //   unsub(); // to stop listening

  onMessage(callback) {
    this._messageSubs.add(callback);
    return () => this._messageSubs.delete(callback);
  }

  onError(callback) {
    this._errorSubs.add(callback);
    return () => this._errorSubs.delete(callback);
  }

  onConnectionChange(callback) {
    this._connectionSubs.add(callback);
    return () => this._connectionSubs.delete(callback);
  }

  onConversations(callback) {
    this._conversationsSubs.add(callback);
    return () => this._conversationsSubs.delete(callback);
  }

  _emitMessage(msg) {
    this._messageSubs.forEach((cb) => cb(msg));
  }

  _emitError(err) {
    this._errorSubs.forEach((cb) => cb(err));
  }

  _emitConnection(isConnected) {
    this._connectionSubs.forEach((cb) => cb(isConnected));
  }

  _emitConversations(list) {
    this._conversationsSubs.forEach((cb) => cb(list));
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  get currentUserId() {
    return this._currentUserId || '';
  }

  /** Deterministic conversation ID: sorted UIDs joined with "-" */
  static buildConversationId(uid1, uid2) {
    const parts = [uid1, uid2].sort();
    return `${parts[0]}-${parts[1]}`;
  }

  /** Deterministic participants key for Firestore queries */
  static buildParticipantsKey(uid1, uid2) {
    const parts = [uid1, uid2].sort();
    return `${parts[0]}_${parts[1]}`;
  }

  // ── Connect ─────────────────────────────────────────────────────────────

  async connect() {
    if (this._initialized) return;

    try {
      const user = auth.currentUser;
      if (!user) {
        this._emitError('User not authenticated');
        this._emitConnection(false);
        return;
      }

      this._currentUserId = user.uid;
      this._initialized = true;
      this._emitConnection(true);

      this._listenToConversations();

      console.log('ChatService connected as', this._currentUserId);
    } catch (e) {
      console.error('ChatService connect error:', e);
      this._emitError(`Connection failed: ${e}`);
      this._emitConnection(false);
    }
  }

  // ── Conversations listener ─────────────────────────────────────────────

  _listenToConversations() {
    if (this._conversationsUnsub) this._conversationsUnsub();

    const q = query(
      collection(db, 'chats'),
      where('participants', 'array-contains', this._currentUserId),
      orderBy('lastMessageTime', 'desc')
    );

    this._conversationsUnsub = onSnapshot(
      q,
      (snapshot) => {
        const conversations = [];
        for (const docSnap of snapshot.docs) {
          const conv = this._conversationFromDoc(docSnap);
          if (conv !== null) {
            conversations.push(conv);
          }
        }
        // ✅ Emit to all subscribers including ConversationsScreen
        this._emitConversations(conversations);
      },
      (e) => {
        console.error('Conversations listener error:', e);
        this._emitError(`Conversations error: ${e}`);
      }
    );
  }

  _conversationFromDoc(docSnap) {
    try {
      const data = docSnap.data();
      if (!data) return null;

      const participants = data.participants || [];
      const otherId = participants.find((p) => p !== this._currentUserId) || '';
      if (!otherId) return null;

      const lastMessageTime = data.lastMessageTime?.toDate?.() || new Date();

      const unreadMap = data.unreadCount || {};
      const unreadCount = unreadMap[this._currentUserId] || 0;

      const rawStatus = data.lastMessageStatus;
      let lastMsgStatus = MessageStatus.sent;
      if (rawStatus === 'delivered') lastMsgStatus = MessageStatus.delivered;
      else if (rawStatus === 'read') lastMsgStatus = MessageStatus.read;
      else if (rawStatus === 'sending') lastMsgStatus = MessageStatus.sending;
      else if (rawStatus === 'failed') lastMsgStatus = MessageStatus.failed;

      return new ChatConversation({
        id: docSnap.id,
        otherUserId: otherId,
        lastMessage: data.lastMessage || '',
        lastMessageTime,
        lastMessageSenderId: data.lastMessageSender || '',
        lastMessageStatus: lastMsgStatus,
        unreadCount,
        participants,
        lastMessageType: data.lastMessageType || 'text',
        isArchived: data.isArchived === true,
        isMuted: data.isMuted === true,
        isPinned: data.isPinned === true,
      });
    } catch (e) {
      console.error('Error parsing conversation doc:', e);
      return null;
    }
  }

  // ── Messages stream (per conversation) ─────────────────────────────────
  //
  // Usage:
  //   const unsub = chatService.getMessagesStream(conversationId, (messages) => {
  //     setMessages(messages);
  //   });
  //   // later: unsub();

  getMessagesStream(conversationId, callback) {
    const q = query(
      collection(db, 'chats', conversationId, 'messages'),
      orderBy('ts', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages = snapshot.docs
          .map((d) => this._messageFromDoc(d, conversationId))
          .filter((m) => m !== null);
        callback(messages);
      },
      (e) => {
        console.error('Messages listener error:', e);
        this._emitError(`Messages error: ${e}`);
      }
    );

    this._messageListeners.set(conversationId, unsubscribe);
    return unsubscribe;
  }

  _messageFromDoc(docSnap, conversationId) {
    try {
      const data = docSnap.data();
      if (!data) return null;

      const ts = data.ts;
      let timestamp;
      if (ts?.toDate) {
        timestamp = ts.toDate();
      } else if (typeof ts === 'string') {
        const parsed = new Date(ts);
        timestamp = isNaN(parsed.getTime()) ? new Date() : parsed;
      } else {
        timestamp = new Date();
      }

      return new ChatMessage({
        id: docSnap.id,
        conversationId,
        senderId: data.from || '',
        receiverId: data.to || '',
        content: data.msg || '',
        timestamp,
        status: this._statusFromString(data.status),
        messageType: this._typeFromString(data.msgType),
        attachmentUrl: data.attachmentUrl || null,
        attachmentType: data.attachmentType || null,
        seenBy: data.seenBy || [],
        isDeleted: data.isDeleted === true,
      });
    } catch (e) {
      console.error('Error parsing message doc:', e);
      return null;
    }
  }

  // ── Send message ────────────────────────────────────────────────────────

  async sendMessage(message) {
    if (!this._currentUserId) throw new Error('Not connected');

    const conversationId = message.conversationId;
    const convRef = doc(db, 'chats', conversationId);
    const msgRef = doc(db, 'chats', conversationId, 'messages', message.id);
    const clientNow = new Date();

    await chatLocalDb.clearDeletedFlag(conversationId);

    const batch = writeBatch(db);

    batch.set(msgRef, {
      from: message.senderId,
      to: message.receiverId,
      msg: message.content,
      ts: Timestamp.fromDate(clientNow),
      msgType: message.messageType,
      status: 'sent',
      ...(message.attachmentUrl ? { attachmentUrl: message.attachmentUrl } : {}),
      ...(message.attachmentType ? { attachmentType: message.attachmentType } : {}),
      seenBy: [message.senderId],
      isDeleted: false,
    });

    batch.set(
      convRef,
      {
        participants: arrayUnion(message.senderId, message.receiverId),
        deletedBy: arrayRemove(message.senderId),
        lastMessage: this._lastMessagePreview(message.content, message.messageType),
        lastMessageSender: message.senderId,
        lastMessageTime: serverTimestamp(),
        lastMessageType: message.messageType,
        lastMessageStatus: 'sent',
        [`unreadCount.${message.receiverId}`]: increment(1),
        [`unreadCount.${message.senderId}`]: 0,
      },
      { merge: true }
    );

    await batch.commit();
  }

  // ── Ensure conversation exists ─────────────────────────────────────────

  async ensureConversation(otherUserId) {
    const myId = this._currentUserId;
    const convId = ChatService.buildConversationId(myId, otherUserId);
    const convRef = doc(db, 'chats', convId);

    await chatLocalDb.clearDeletedFlag(convId);

    const snap = await getDocs(query(collection(db, 'chats'), where('__name__', '==', convId)));
    const exists = !snap.empty;

    if (!exists) {
      const sortedParticipants = [myId, otherUserId].sort();
      await setDoc(convRef, {
        participants: sortedParticipants,
        participantsKey: ChatService.buildParticipantsKey(myId, otherUserId),
        lastMessage: '',
        lastMessageSender: '',
        lastMessageTime: serverTimestamp(),
        lastMessageType: 'text',
        lastMessageStatus: 'sent',
        unreadCount: { [myId]: 0, [otherUserId]: 0 },
        isArchived: false,
        isMuted: false,
        isPinned: false,
        mutedUntil: null,
        lastSeen: {},
        createdAt: serverTimestamp(),
        deletedAt: null,
        deletedBy: [],
        allParticipants: sortedParticipants,
      });
    } else {
      const data = snap.docs[0].data() || {};
      const participants = data.participants || [];
      if (!participants.includes(myId)) {
        await updateDoc(convRef, {
          participants: arrayUnion(myId),
          deletedBy: arrayRemove(myId),
        });
      }
    }

    return convId;
  }

  // ── Mark conversation as read ──────────────────────────────────────────

  async markConversationAsRead(conversationId) {
    const myId = this._currentUserId;
    if (!myId) return;

    try {
      await setDoc(
        doc(db, 'chats', conversationId),
        {
          [`unreadCount.${myId}`]: 0,
          [`lastSeen.${myId}`]: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.error('markAsRead error:', e);
    }
  }

  // ── Fetch conversations (one-shot) ─────────────────────────────────────

  async fetchConversations() {
    try {
      const q = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', this._currentUserId),
        orderBy('lastMessageTime', 'desc')
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => this._conversationFromDoc(d)).filter((c) => c !== null);
    } catch (e) {
      console.error('fetchConversations error:', e);
      return [];
    }
  }

  // ── Disconnect / dispose ────────────────────────────────────────────────

  async disconnect() {
    if (this._conversationsUnsub) {
      this._conversationsUnsub();
      this._conversationsUnsub = null;
    }
    this._messageListeners.forEach((unsub) => unsub());
    this._messageListeners.clear();

    this._initialized = false;
    this._currentUserId = null;
    this._emitConnection(false);
  }

  dispose() {
    this.disconnect();
    this._messageSubs.clear();
    this._errorSubs.clear();
    this._connectionSubs.clear();
    this._conversationsSubs.clear();
  }

  // ── Static helpers ──────────────────────────────────────────────────────

  static _statusFromStringImpl(v) {
    switch (v) {
      case 'sent':
        return MessageStatus.sent;
      case 'delivered':
        return MessageStatus.delivered;
      case 'read':
        return MessageStatus.read;
      case 'failed':
        return MessageStatus.failed;
      default:
        return MessageStatus.sending;
    }
  }

  _statusFromString(v) {
    return ChatService._statusFromStringImpl(v);
  }

  _typeFromString(v) {
    switch (v) {
      case 'image':
        return MessageType.image;
      case 'audio':
        return MessageType.audio;
      case 'video':
        return MessageType.video;
      case 'file':
        return MessageType.file;
      case 'deleted':
        return MessageType.deleted;
      default:
        return MessageType.text;
    }
  }

  _lastMessagePreview(content, type) {
    switch (type) {
      case MessageType.image:
        return '📷 Photo';
      case MessageType.audio:
        return '🎤 Voice message';
      case MessageType.video:
        return '🎥 Video';
      case MessageType.file:
        return `📎 ${content || 'File'}`;
      case MessageType.deleted:
        return 'This message was deleted';
      default:
        return content;
    }
  }
}

// ── Singleton export ────────────────────────────────────────────────────────

const chatService = ChatService.getInstance();
export { chatService };
export default chatService;